// Convert a page-style video URL (Vimeo, YouTube) into its embeddable
// player URL, with autoplay turned on. Vimeo's vimeo.com/{id} pages send
// X-Frame-Options: sameorigin and refuse to load in an iframe — they must
// be swapped for player.vimeo.com/video/{id}. YouTube watch/share URLs need
// the same treatment. Anything else (e.g. Matterport tours, links already
// pointing at an embed/player domain) is passed through, only gaining the
// autoplay param when we recognize the host.
function getEmbeddableTourUrl(url) {
    if (!url) return url;

    try {
        const parsed = new URL(url, window.location.href);
        const host = parsed.hostname.replace(/^www\./, '');

        // Vimeo: vimeo.com/123456789(/unlisted-hash)? → player.vimeo.com/video/123456789(?h=hash)
        if (host === 'vimeo.com') {
            const parts = parsed.pathname.split('/').filter(Boolean);
            const videoId = parts[0];
            const hash = parts[1];
            if (videoId && /^\d+$/.test(videoId)) {
                const embed = new URL(`https://player.vimeo.com/video/${videoId}`);
                if (hash) embed.searchParams.set('h', hash);
                embed.searchParams.set('autoplay', '1');
                return embed.toString();
            }
        }

        // Already a Vimeo player URL — just make sure autoplay is on.
        if (host === 'player.vimeo.com') {
            parsed.searchParams.set('autoplay', '1');
            return parsed.toString();
        }

        // YouTube: youtube.com/watch?v=ID or youtu.be/ID → youtube.com/embed/ID
        if (host === 'youtube.com' || host === 'youtu.be') {
            let videoId = '';
            if (host === 'youtu.be') {
                videoId = parsed.pathname.split('/').filter(Boolean)[0] || '';
            } else if (parsed.pathname === '/watch') {
                videoId = parsed.searchParams.get('v') || '';
            } else if (parsed.pathname.startsWith('/embed/')) {
                parsed.searchParams.set('autoplay', '1');
                return parsed.toString(); // already an embed URL
            }
            if (videoId) {
                const embed = new URL(`https://www.youtube.com/embed/${videoId}`);
                embed.searchParams.set('autoplay', '1');
                return embed.toString();
            }
        }
    } catch (e) {
        // Not a parseable absolute URL — fall through and use as-is.
    }

    return url;
}

function initLightbox() {
    const lightbox = document.getElementById('galleryLightbox');
    if (!lightbox) return;

    const backdrop  = lightbox.querySelector('.lightbox__backdrop');
    const closeBtn  = lightbox.querySelector('[data-lightbox-close]');
    const prevBtn   = lightbox.querySelector('[data-lightbox-prev]');
    const nextBtn   = lightbox.querySelector('[data-lightbox-next]');
    const imgEl     = lightbox.querySelector('[data-lightbox-image]');
    const iframeEl  = lightbox.querySelector('[data-lightbox-iframe]'); // ← new
    const captionEl = lightbox.querySelector('[data-lightbox-caption]');

    let currentImages = [];
    let currentIndex  = 0;

    // ── Collect clickable images from all gallery sliders ──
    const galleries = document.querySelectorAll('[data-slider-gallery]');

    galleries.forEach((gallery) => {
        const slides  = gallery.querySelectorAll('.slider-gallery__slide img');
        const carousel = gallery.querySelector('[data-gallery-carousel]');
        const flkty   = Flickity.data(carousel);

        if (flkty) {
            slides.forEach((img) => { img.style.cursor = 'pointer'; });

            flkty.on('staticClick', (event, pointer, cellElement, cellIndex) => {
                if (cellIndex === undefined) return;

                currentImages = Array.from(slides).map((i) => ({
                    src:      i.getAttribute('src'),
                    alt:      i.getAttribute('alt') || '',
                    tourLink: getEmbeddableTourUrl(i.getAttribute('data-tour-link')) || null, // ← new
                    caption:  i.getAttribute('data-caption') || '',
                }));
                currentIndex = cellIndex;
                open();
            });
        }
    });

    // ── Open / Close ──
    function open() {
        showImage();
        lightbox.classList.add('is-active');
        lightbox.setAttribute('aria-hidden', 'false');
        document.body.classList.add('lightbox-open');
        document.addEventListener('keydown', onKeydown);
    }

    function close() {
        lightbox.classList.remove('is-active');
        lightbox.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('lightbox-open');
        document.removeEventListener('keydown', onKeydown);

        // Stop iframe playback on close
        if (iframeEl) {
            iframeEl.src = '';
            iframeEl.style.display = 'none';
        }
        imgEl.style.display = '';
    }

    // ── Navigation ──
    function showImage() {
        const item = currentImages[currentIndex];
        if (!item) return;

        setCaption(item.tourLink ? '' : item.caption);

        if (item.tourLink) {
            // ── Tour / video mode ──
            imgEl.style.display = 'none';
            iframeEl.style.display = '';
            iframeEl.src = item.tourLink;
            lightbox.classList.add('is-tour');
        } else {
            // ── Image mode ──
            if (iframeEl) {
                iframeEl.src = '';
                iframeEl.style.display = 'none';
            }
            lightbox.classList.remove('is-tour');
            imgEl.style.display = '';
            imgEl.classList.remove('is-loaded');
            imgEl.src = item.src;
            imgEl.alt = item.alt;

            if (imgEl.complete) {
                imgEl.classList.add('is-loaded');
            } else {
                imgEl.onload = () => imgEl.classList.add('is-loaded');
            }
        }

        updateArrows();
    }

    function setCaption(text) {
        if (!captionEl) return;
        captionEl.textContent = text || '';
        captionEl.hidden = !text;
    }

    function prev() {
        if (currentIndex > 0) { currentIndex--; showImage(); }
    }

    function next() {
        if (currentIndex < currentImages.length - 1) { currentIndex++; showImage(); }
    }

    function updateArrows() {
        prevBtn.disabled = currentIndex === 0;
        nextBtn.disabled = currentIndex === currentImages.length - 1;
    }

    // ── Event listeners ──
    closeBtn.addEventListener('click', close);
    backdrop.addEventListener('click', close);
    prevBtn.addEventListener('click', prev);
    nextBtn.addEventListener('click', next);

    function onKeydown(e) {
        if (e.key === 'Escape')     close();
        if (e.key === 'ArrowLeft')  prev();
        if (e.key === 'ArrowRight') next();
    }

    // ── Swipe support (touch) ──
    let touchStartX = 0;
    let touchStartY = 0;
    let isSwiping   = false;
    let touchMoved  = false;

    const stage = lightbox.querySelector('.lightbox__stage');

    stage.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].clientX;
        touchStartY = e.changedTouches[0].clientY;
        isSwiping   = true;
        touchMoved  = false;
    }, { passive: true });

    stage.addEventListener('touchmove', () => {
        touchMoved = true;
    }, { passive: true });

    stage.addEventListener('touchend', (e) => {
        if (!isSwiping) return;
        isSwiping = false;

        const deltaX   = e.changedTouches[0].clientX - touchStartX;
        const deltaY   = e.changedTouches[0].clientY - touchStartY;
        const minSwipe = 50;

        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > minSwipe) {
            deltaX < 0 ? next() : prev();
        }
    }, { passive: true });

    // ── Click to advance ──
    stage.addEventListener('click', (e) => {
        if (touchMoved) return;
        if (e.target.closest('.lightbox__arrow') || e.target.closest('.lightbox__close')) return;
        // Don't advance on click when showing a tour iframe
        if (currentImages[currentIndex]?.tourLink) return;

        if (currentIndex < currentImages.length - 1) {
            next();
        } else {
            close();
        }
    });
}